import { type PropsWithChildren, createContext, useContext, useEffect, useMemo, useState } from 'react';

import type DeviceService from './device.service';

export interface DeviceContextValues {
  deviceToken?: string;
}

const DeviceContext = createContext<DeviceContextValues>({
  deviceToken: undefined,
});

export type DeviceProviderProps = PropsWithChildren<{
  deviceServiceInstance: DeviceService;
}>;

export default function DeviceProvider({ children, deviceServiceInstance }: DeviceProviderProps) {
  const [deviceToken, setDeviceToken] = useState<string | undefined>(undefined);

  useEffect(() => {
    const deviceListener = deviceServiceInstance.addDeviceTokenListener((newToken) => setDeviceToken(newToken));
    setDeviceToken(deviceServiceInstance.getDeviceToken());
    return () => deviceServiceInstance.removeDeviceTokenListener(deviceListener);
    // We do not need to change if the device service instance changes as it's a singleton.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const context = useMemo(() => ({ deviceToken }), [deviceToken]);

  return <DeviceContext.Provider value={context}>{children}</DeviceContext.Provider>;
}

export function useDevice() {
  return useContext(DeviceContext);
}
